import { API_BASE_URL } from '../config/index.js';
import { getRawInitData } from '../telegram/index.js';

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

type ApiRequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
};

const readErrorMessage = async (response: Response): Promise<string> => {
  try {
    const payload: unknown = await response.json();

    if (typeof payload === 'object' && payload !== null && 'error' in payload) {
      const { error } = payload;

      if (typeof error === 'string') {
        return error;
      }
    }
  } catch {
    // The server answered with something that is not JSON; the status is all we have left.
  }

  return `Запит завершився помилкою ${String(response.status)}`;
};

const buildHeaders = (hasBody: boolean): Headers => {
  const headers = new Headers({ Accept: 'application/json' });
  const rawInitData = getRawInitData();

  if (rawInitData !== null) {
    headers.set('Authorization', `tma ${rawInitData}`);
  }

  if (hasBody) {
    headers.set('Content-Type', 'application/json');
  }

  return headers;
};

/**
 * fetch rejects with the browser's own TypeError when the connection fails, and every error state
 * in the app prints `message` unchanged - so "Failed to fetch" would reach a person in English.
 * An abort is not a failure: TanStack Query relies on it to tell a cancelled query apart.
 */
const send = async (url: string, init: RequestInit): Promise<Response> => {
  try {
    return await fetch(url, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }

    throw new ApiError(0, 'Немає зв’язку з сервером. Перевірте інтернет і спробуйте ще раз.');
  }
};

export const apiRequest = async <T>(path: string, options: ApiRequestOptions = {}): Promise<T> => {
  const { method = 'GET', body, signal } = options;
  const hasBody = body !== undefined;

  const response = await send(`${API_BASE_URL}${path}`, {
    method,
    headers: buildHeaders(hasBody),
    body: hasBody ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!response.ok) {
    throw new ApiError(response.status, await readErrorMessage(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
};
